import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Briefcase, MapPin, Clock, ShieldCheck, CheckCircle2,
  Send, Plus, Sparkles, Building2, Search, ArrowRight, ExternalLink, RefreshCw, Camera, User, Lock
} from "lucide-react";
import { getTheme, fonts } from "./../theme";
import Navbar from "./../components/Navbar";
import { Button } from "./../components/UI";
import BreakdownGrid from "./../components/BreakdownGrid";
import TrustDial from "./../components/TrustDial";
import api, { fileUrl } from "./../api";
import { EMPLOYER_CRITERIA } from "./../criteria";
import { normalizeOpenPosition } from "./RecruiterDashboard";
import { rankPositions, yearsLabel } from "./../positionSearch";

export default function JobSeekerDashboard({ user: propUser, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("job_seeker", themeMode);
  const isLight = theme.key === "light";
  const navigate = useNavigate();

  const user = propUser || (() => {
    try {
      const s = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
      return s && s !== "undefined" ? JSON.parse(s) : null;
    } catch (e) { return null; }
  })();

  const [seekerData, setSeekerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [linkedinJobs, setLinkedinJobs] = useState([]);
  const [linkedinField, setLinkedinField] = useState("");
  const [linkedinJobsMessage, setLinkedinJobsMessage] = useState("");
  const [linkedinSearchUrl, setLinkedinSearchUrl] = useState("");
  const [checkingPositions, setCheckingPositions] = useState(false);
  const [showCheckForm, setShowCheckForm] = useState(true);
  const [showLinkedInJobs, setShowLinkedInJobs] = useState(false);
  const [jobTitle, setJobTitle] = useState("");
  const [requiredSkills, setRequiredSkills] = useState("");
  const [desiredSkills, setDesiredSkills] = useState("");
  const [expYearsMin, setExpYearsMin] = useState("");
  const [jobLocation, setJobLocation] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("both");
  const [targetCompany, setTargetCompany] = useState("");
  const [openPositions, setOpenPositions] = useState([]);
  const [fetchMessage, setFetchMessage] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [searchFallback, setSearchFallback] = useState(false);
  const [companyRatings, setCompanyRatings] = useState(null);
  const [companyRatingsLoading, setCompanyRatingsLoading] = useState(false);
  const [companyRatingsError, setCompanyRatingsError] = useState("");
  const openPositionsRef = useRef(null);
  const linkedinJobsRef = useRef(null);
  const photoInputRef = useRef(null);
  const [identityPanel, setIdentityPanel] = useState(null);
  const [identityBusy, setIdentityBusy] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const [identityOk, setIdentityOk] = useState("");
  const [aadhaarFrontName, setAadhaarFrontName] = useState("");
  const [aadhaarBackName, setAadhaarBackName] = useState("");
  const [aadhaarPreview, setAadhaarPreview] = useState("");
  const [uanDraft, setUanDraft] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");

  const statusStyle = (status) => {
    const key = String(status || "").toLowerCase();
    if (key === "hired" || key === "offer") return { label: "Hired", color: "#0D6EFD" };
    if (key === "shortlisted") return { label: "Shortlisted", color: isLight ? "#16A34A" : "#22C55E" };
    if (key === "interviewed" || key === "interview") return { label: "Interview", color: theme.accent };
    if (key === "rejected") return { label: "Rejected", color: "#DC2626" };
    return { label: status || "Applied", color: theme.accent };
  };

  const loadFieldJobs = async (profileLike = {}) => {
    const dummyProfile = /verified professional/i.test(String(profileLike.name || ""));
    const rawTitle = dummyProfile ? "" : String(profileLike.title || "").trim();
    const genericTitle = /^(professional|verified professional|software\s*&\s*tech professional|job seeker|candidate)$/i.test(rawTitle);
    try {
      const live = await api.getJobSeekerLinkedInJobs({
        title: genericTitle ? "" : rawTitle,
        location: profileLike.location || "",
        skills: profileLike.strengths || "",
        employeeId: user?.id
      });
      const li = Array.isArray(live?.jobs) ? live.jobs : [];
      const wibil = Array.isArray(live?.wibilJobs) ? live.wibilJobs : [];
      setLinkedinJobs(li);
      setMatchedJobs(wibil);
      setLinkedinField(live?.field || (!genericTitle && rawTitle) || "your field");
      setLinkedinJobsMessage(live?.message || "");
      setLinkedinSearchUrl(live?.linkedinSearchUrl || "");
    } catch (e) {
      setLinkedinJobs([]);
      setLinkedinJobsMessage("Could not load LinkedIn jobs for your field.");
    }
  };

  const loadSeekerProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (user?.id) {
        const res = await api.get(`/employees/${user.id}`);
        setSeekerData(res);
        try {
          const apps = await api.getSeekerApplications(user.id);
          setAppliedJobs(Array.isArray(apps) ? apps : []);
        } catch (e) {
          setAppliedJobs([]);
        }
        setShowCheckForm(true);
        setShowLinkedInJobs(false);
      } else {
        setSeekerData({
          profile: {
            name: user?.name || "Job Seeker",
            title: user?.title || "",
            email: user?.email || "",
            phone: "",
            location: user?.location || "",
            strengths: ""
          },
          score: { trust_score: 650, rating_count: 0 }
        });
        setAppliedJobs([]);
        setShowCheckForm(true);
        setShowLinkedInJobs(false);
      }
    } catch (err) {
      console.error(err);
      setSeekerData({
        profile: {
          name: user?.name || "Job Seeker",
          title: user?.title || "",
          email: user?.email || "",
          phone: "",
          location: user?.location || "",
          strengths: ""
        },
        score: { trust_score: 650, rating_count: 0 }
      });
      setAppliedJobs([]);
      setShowCheckForm(true);
      setShowLinkedInJobs(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.name, user?.email, user?.title, user?.location]);

  useEffect(() => { loadSeekerProfile(); }, [loadSeekerProfile]);

  const checkOpenPositions = () => {
    setShowCheckForm(true);
    setTimeout(() => {
      openPositionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const closeCheckForm = () => {
    setShowCheckForm(false);
    setOpenPositions([]);
    setFetchMessage("");
    setFetchError("");
    setSearchFallback(false);
    setCompanyRatings(null);
    setCompanyRatingsError("");
  };

  const firstCompanyName = (raw) => {
    const parts = String(raw || "").split(",");
    for (const part of parts) {
      const name = String(part || "").trim();
      if (name) return name;
    }
    return "";
  };

  const loadCompanyRatings = async (companyRaw) => {
    const company = firstCompanyName(companyRaw);
    if (company.length < 2) {
      setCompanyRatings(null);
      setCompanyRatingsError("");
      return;
    }
    setCompanyRatingsLoading(true);
    setCompanyRatingsError("");
    try {
      const res = await api.getEmployerWorkplaceSummary(company);
      setCompanyRatings(res || null);
    } catch (err) {
      setCompanyRatings(null);
      setCompanyRatingsError(err.message || "Could not load employee ratings for this company.");
    } finally {
      setCompanyRatingsLoading(false);
    }
  };

  const showLinkedInFieldJobs = async () => {
    setShowLinkedInJobs(true);
    await loadFieldJobs({
      name: profile.name || "",
      title: profile.title || "",
      location: profile.location || "",
      strengths: profile.strengths || ""
    });
    setTimeout(() => {
      linkedinJobsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const fetchOpenPositions = async () => {
    const company = targetCompany.trim();
    if (!company) {
      setFetchError("Enter at least one company name, separated by commas if you have more than one.");
      return;
    }
    setCheckingPositions(true);
    setFetchError("");
    setFetchMessage("");
    setSearchFallback(false);
    setOpenPositions([]);
    const query = {
      title: jobTitle.trim(),
      skills: requiredSkills.trim(),
      experienceYears: String(expYearsMin || "").trim(),
      location: jobLocation.trim(),
      company
    };
    try {
      const [res] = await Promise.all([
        api.scrapeCareerPages({
          title: "",
          company,
          location: "",
          experience: "both",
          experienceYearsMin: "",
          skills: "",
          desiredSkills: "",
          count: 80,
          platforms: ["career"],
          allCompanyJobs: true
        }),
        loadCompanyRatings(company)
      ]);
      const raw = (res?.jobs || res?.openPositions || [])
        .map((j, i) => normalizeOpenPosition(j, i))
        .filter((j) => j.title);
      const ranked = rankPositions(raw, query);
      const positions = ranked.positions.length ? ranked.positions : raw;
      setOpenPositions(positions);
      setSearchFallback(false);
      if (!positions.length) {
        setFetchError(res?.error || res?.message || `No open positions were found at ${company} right now.`);
      } else {
        setFetchMessage(`Showing all ${positions.length} open position(s) at ${company}.`);
      }
    } catch (err) {
      setFetchError(err.message || "Could not search positions for those companies.");
    } finally {
      setCheckingPositions(false);
    }
  };

  const profile = seekerData?.profile || seekerData || {};
  const seekerName = profile.name || user?.name || "Job Seeker";
  const ratingScore = companyRatings?.score || {};
  const privateRatingCount = Number(companyRatings?.private_rating_count || ratingScore.rating_count || 0);
  const hasWorkplaceScores = privateRatingCount > 0;
  const workplaceTrustScore = workplaceTrustFromScore(ratingScore, privateRatingCount);
  const ratingsLegalName = companyRatings?.legal_name || companyRatings?.company_name || "";
  const ratingsTradeName = companyRatings?.trade_name || "";
  const ratingsCompanyLabel = ratingsTradeName && ratingsLegalName && ratingsTradeName.toLowerCase() !== ratingsLegalName.toLowerCase()
    ? `${ratingsTradeName} · ${ratingsLegalName}`
    : (ratingsLegalName || ratingsTradeName || firstCompanyName(targetCompany));

  useEffect(() => {
    if (profile.uan) setUanDraft(String(profile.uan));
  }, [profile.uan]);

  const applyIdentityProfile = (next) => {
    setSeekerData((prev) => ({
      ...(prev || {}),
      profile: { ...((prev && prev.profile) || prev || {}), ...next }
    }));
  };

  const saveIdentity = async (body) => {
    if (!user?.id) throw new Error("Please sign in to update identity details.");
    const res = await api.updateSeekerIdentity(user.id, body);
    if (res?.profile) applyIdentityProfile(res.profile);
    return res;
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

  const toggleIdentityPanel = (panel) => {
    if (panel === "aadhaar" && profile.aadhaar_last4) return;
    if (panel === "uan" && profile.uan) return;
    setIdentityPanel((cur) => (cur === panel ? null : panel));
    setIdentityError("");
    setIdentityOk("");
  };

  const handleAadhaarUpload = async (e, side) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (profile.aadhaar_last4) {
      setIdentityError("Aadhaar eKYC is already verified and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (side === "front") setAadhaarFrontName(file.name);
    else setAadhaarBackName(file.name);
    setIdentityError("");
    setIdentityOk("");
    setIdentityBusy(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const contentType = /png/i.test(file.type) ? "PNG" : "JPEG";
      const res = await api.verifyAadhaarQr(dataUrl, contentType, seekerName, { strict: true });
      const fields = res?.data?.fields || res?.fields || {};
      const last4 = String(fields.aadhaar_last4 || "").replace(/\D/g, "").slice(-4);
      if (last4.length !== 4) {
        throw new Error("Aadhaar QR did not return last 4 digits. Upload a clear image of the QR side.");
      }
      if (res?.data?.photo?.base64) {
        setAadhaarPreview(`data:${res.data.photo.mime_type || "image/jpeg"};base64,${res.data.photo.base64}`);
      }
      await saveIdentity({ aadhaar_last4: last4 });
      setIdentityOk("Aadhaar eKYC verified.");
      setIdentityPanel(null);
    } catch (err) {
      setIdentityError(err.message || "Aadhaar verification failed.");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const handleUanSave = async () => {
    if (profile.uan) {
      setIdentityError("EPFO / UAN is already linked and cannot be changed.");
      return;
    }
    const uan = String(uanDraft || "").replace(/\s+/g, "").toUpperCase();
    if (uan.length < 8) {
      setIdentityError("Enter a valid UAN / EPF number (at least 8 characters).");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      await api.post("/verify/epf", { epfNo: uan });
      await saveIdentity({ uan });
      setIdentityOk("EPFO / UAN verified.");
      setIdentityPanel(null);
    } catch (err) {
      setIdentityError(err.message || "Could not link UAN.");
    } finally {
      setIdentityBusy(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const stored = String(profile.profile_photo || profile.profilePhoto || "").trim();
    if (stored || photoPreview) {
      setIdentityError("Profile photo is already set and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      setIdentityError("Please upload a JPG, PNG, or WebP photo.");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPhotoPreview(dataUrl);
      await saveIdentity({ profilePhoto: dataUrl });
      setIdentityOk("Profile photo saved.");
    } catch (err) {
      setIdentityError(err.message || "Could not save profile photo.");
      setPhotoPreview("");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const storedPhoto = String(profile.profile_photo || profile.profilePhoto || "").trim();
  const photoSrc = photoPreview
    || (storedPhoto.startsWith("data:") || storedPhoto.startsWith("http") ? storedPhoto : fileUrl(storedPhoto))
    || "";
  const updateBtnStyle = {
    border: `1px solid ${theme.line}`,
    color: theme.accent,
    background: "transparent",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 10px"
  };
  const updateActionBtnStyle = {
    background: "#0D6EFD",
    color: "#FFFFFF",
    border: "1px solid #0D6EFD",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 12px",
    borderRadius: 6
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body, transition: "all 0.2s" }}>
      <Navbar theme={theme} userName={seekerName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div className="container py-4 py-md-5">
        
        {/* Candidate Job Seeker Banner Header */}
        <div 
          className="card p-4 rounded-4 shadow-sm mb-4" 
          style={{ 
            background: theme.surface, 
            border: `1px solid ${theme.line}`, 
            color: theme.text,
            boxShadow: isLight ? "0 4px 20px -2px rgba(0, 0, 0, 0.05)" : "none"
          }}
        >
          <div>
            {/* Profile Info & Stats */}
            <div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    onClick={() => { if (!storedPhoto && !photoPreview) photoInputRef.current?.click(); }}
                    title={storedPhoto || photoPreview ? "Profile photo is already set" : "Add profile photo"}
                    className="border-0 p-0 position-relative"
                    disabled={identityBusy || !user?.id || Boolean(storedPhoto || photoPreview)}
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "transparent",
                      cursor: user?.id ? "pointer" : "default",
                      flexShrink: 0
                    }}
                  >
                    <span
                      className="d-block w-100 h-100"
                      style={{
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: theme.surfaceRaised,
                        border: `2px solid ${theme.line}`
                      }}
                    >
                      {photoSrc ? (
                        <img src={photoSrc} alt={seekerName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span className="d-flex align-items-center justify-content-center w-100 h-100" style={{ color: theme.textMute }}>
                          <User size={32} />
                        </span>
                      )}
                    </span>
                    {!storedPhoto && !photoPreview && (
                    <span
                      className="position-absolute d-flex align-items-center justify-content-center"
                      style={{
                        right: 0,
                        bottom: 0,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: theme.accent,
                        color: "#fff",
                        border: "2px solid #fff"
                      }}
                    >
                      <Camera size={12} />
                    </span>
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={handlePhotoChange}
                  />
                  <div>
                    <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-3 py-1.5 rounded-pill small fw-bold mb-2 d-inline-flex align-items-center gap-1">
                      <Briefcase size={13} /> JOB SEEKER CANDIDATE DASHBOARD
                    </span>
                    <h2 className="h3 fw-bold mb-1" style={{ fontFamily: fonts.display, color: theme.text }}>{seekerName}</h2>
                    <div className="small d-flex flex-wrap align-items-center gap-3 text-secondary">
                      <span>{profile.title || "Software & Tech Professional"}</span>
                      {profile.location && <span className="d-inline-flex align-items-center gap-1"><MapPin size={13} />{profile.location}</span>}
                      <span className="d-inline-flex align-items-center gap-1"><Clock size={13} /> Notice: {profile.notice_period || "30 Days"}</span>
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <Button theme={theme} onClick={checkOpenPositions}>
                    <Briefcase size={14} /> Check Open Positions
                  </Button>
                  <Button theme={theme} variant="ghost" onClick={showLinkedInFieldJobs} style={{ borderColor: theme.accent, color: theme.accent }}>
                    <Search size={14} /> LinkedIn Jobs in my field
                  </Button>
                </div>
              </div>

              <div className="p-3 rounded-3 mt-3" style={{ background: theme.surfaceRaised, border: `1px solid ${theme.line}` }}>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2 small fw-semibold">
                    <ShieldCheck size={16} color={profile.aadhaar_last4 ? "#16A34A" : "#DC2626"} />
                    <span>Aadhaar eKYC:{" "}
                      <strong style={{ color: profile.aadhaar_last4 ? "#16A34A" : "#DC2626" }}>
                        {profile.aadhaar_last4 ? "Verified" : "Not verified"}
                      </strong>
                    </span>
                    {!profile.aadhaar_last4 && (
                      <button type="button" className="btn btn-sm" disabled={identityBusy || !user?.id} onClick={() => toggleIdentityPanel("aadhaar")} style={updateActionBtnStyle}>
                        Update
                      </button>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 small fw-semibold">
                    <CheckCircle2 size={16} color={profile.uan ? "#16A34A" : "#DC2626"} />
                    <span>EPFO / UAN:{" "}
                      <strong style={{ color: profile.uan ? "#16A34A" : "#DC2626" }}>
                        {profile.uan ? "Verified" : "Not linked"}
                      </strong>
                    </span>
                    {!profile.uan && (
                      <button type="button" className="btn btn-sm" disabled={identityBusy || !user?.id} onClick={() => toggleIdentityPanel("uan")} style={updateActionBtnStyle}>
                        Update
                      </button>
                    )}
                  </div>
                </div>
                {identityError && <div className="small mt-2 fw-semibold" style={{ color: "#DC2626" }}>{identityError}</div>}
                {identityOk && <div className="small mt-2 fw-semibold" style={{ color: "#16A34A" }}>{identityOk}</div>}
                {identityBusy && <div className="small mt-2" style={{ color: theme.textMute }}>Updating identity details…</div>}
                {identityPanel === "aadhaar" && !profile.aadhaar_last4 && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.line}` }}>
                    <div className="small fw-bold mb-2">Upload Aadhaar front and the QR side to verify eKYC</div>
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <label className="btn btn-sm w-100" style={{ ...updateBtnStyle, padding: "8px 10px" }}>
                          {aadhaarFrontName || "Upload front image"}
                          <input type="file" accept="image/jpeg,image/png" hidden disabled={identityBusy} onChange={(e) => handleAadhaarUpload(e, "front")} />
                        </label>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="btn btn-sm w-100" style={{ ...updateBtnStyle, padding: "8px 10px" }}>
                          {aadhaarBackName || "Upload QR / back image"}
                          <input type="file" accept="image/jpeg,image/png" hidden disabled={identityBusy} onChange={(e) => handleAadhaarUpload(e, "back")} />
                        </label>
                      </div>
                    </div>
                    {aadhaarPreview && (
                      <img src={aadhaarPreview} alt="" className="rounded-circle mt-2" style={{ width: 48, height: 48, objectFit: "cover" }} />
                    )}
                  </div>
                )}
                {identityPanel === "uan" && !profile.uan && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.line}` }}>
                    <div className="small fw-bold mb-2">Enter UAN / EPF number to link EPFO</div>
                    <div className="d-flex flex-wrap gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm shadow-none"
                        style={{ maxWidth: 260 }}
                        value={uanDraft}
                        onChange={(e) => setUanDraft(e.target.value.replace(/\s+/g, ""))}
                        placeholder="12-digit UAN"
                        disabled={identityBusy}
                      />
                      <Button theme={theme} onClick={handleUanSave} disabled={identityBusy || String(uanDraft || "").length < 8}>
                        Verify & save
                      </Button>
                    </div>
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>

        {showCheckForm && (
        <div className="mb-5" ref={openPositionsRef}>
          <div className="row g-4 mb-3">
            <div className="col-lg-6">
          <div
            className="card p-4 p-md-4 rounded-4 shadow-sm h-100"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.line}`,
              color: theme.text
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-1">
              <h4 className="h5 fw-bold mb-0" style={{ color: theme.text, fontFamily: fonts.display }}>
                Position Search
              </h4>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeCheckForm}>
                Close
              </button>
            </div>
            <p className="small mb-3" style={{ color: theme.textMute }}>
              Enter a company name to list every open position there. Title, skills, experience, and location only rank those results — they do not hide jobs. Comma-separated values in one field are OR.
            </p>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label small fw-bold mb-1">Company Name</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 14 }}
                  placeholder="GoldPE.AI, TCS, Infosys"
                  value={targetCompany}
                  autoFocus
                  onChange={e => {
                    const value = e.target.value;
                    setTargetCompany(value);
                    if (!value.trim()) {
                      setOpenPositions([]);
                      setFetchMessage("");
                      setFetchError("");
                      setSearchFallback(false);
                      setCompanyRatings(null);
                      setCompanyRatingsError("");
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") fetchOpenPositions(); }}
                />
              </div>
              <div className="col-12">
                <label className="form-label small fw-bold mb-1">Target Job Title</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 14 }}
                  placeholder="Full Stack Developer, Software Developer, Project Manager"
                  value={jobTitle}
                  onChange={e => setJobTitle(e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label small fw-bold mb-1">Required Skills</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 14 }}
                  placeholder="C#, .NET Core, React, SQL Server, Azure"
                  value={requiredSkills}
                  onChange={e => setRequiredSkills(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold mb-1">Experience</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 14 }}
                  placeholder="2, 3, 4"
                  value={expYearsMin}
                  onChange={e => setExpYearsMin(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold mb-1">Location</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 14 }}
                  placeholder="Chennai, Pune, Bangalore"
                  value={jobLocation}
                  onChange={e => setJobLocation(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold mb-1" style={{ color: theme.textMute }}>Desired Skills (optional)</label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 13 }}
                  placeholder="EF Core, Angular"
                  value={desiredSkills}
                  onChange={e => setDesiredSkills(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold mb-1" style={{ color: theme.textMute }}>Level (optional)</label>
                <select
                  className="form-select shadow-none"
                  style={{ background: theme.surfaceRaised, color: theme.text, borderColor: theme.line, fontSize: 13 }}
                  value={experienceLevel}
                  onChange={e => setExperienceLevel(e.target.value)}
                >
                  <option value="both">Fresher + Experienced</option>
                  <option value="fresher">Freshers only</option>
                  <option value="experienced">Experienced only</option>
                </select>
              </div>
              <div className="col-12">
                <button
                  type="button"
                  onClick={fetchOpenPositions}
                  disabled={checkingPositions}
                  className="btn btn-primary w-100 fw-bold py-2 rounded-2 d-flex align-items-center justify-content-center gap-2"
                  style={{ background: theme.accent, color: "#fff", border: "none" }}
                >
                  {checkingPositions ? <RefreshCw size={16} className="spin-animation" /> : <Search size={16} />}
                  <span>{checkingPositions ? "Searching positions..." : "Search Positions"}</span>
                </button>
              </div>
              <div className="col-12">
                {fetchError && (
                  <div className="small fw-semibold text-danger">{fetchError}</div>
                )}
                {fetchMessage && !fetchError && (
                  <div className="small fw-semibold" style={{ color: theme.accent }}>{fetchMessage}</div>
                )}
                {searchFallback && openPositions.length > 0 && (
                  <div className="small mt-1" style={{ color: theme.textMute }}>
                    No row matched every field, so closest ranked matches are shown.
                  </div>
                )}
              </div>
            </div>
          </div>
            </div>
            <div className="col-lg-6">
              <div
                className="card p-4 p-md-4 rounded-4 shadow-sm h-100"
                style={{
                  background: theme.surface,
                  border: `1px solid ${theme.line}`,
                  color: theme.text
                }}
              >
                <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                  <div className="flex-grow-1 min-w-0 pe-1">
                    <h4 className="h5 fw-bold mb-1" style={{ color: theme.text, fontFamily: fonts.display }}>
                      Employee ratings
                    </h4>
                    <p className="small mb-0" style={{ color: theme.textMute }}>
                      {ratingsCompanyLabel
                        ? `Workplace scores employees gave ${ratingsCompanyLabel}. GST legal name and career-site brand are treated as the same employer. Comments and proof stay private.`
                        : "Search a company name to see employee ratings for that employer."}
                    </p>
                  </div>
                  {(hasWorkplaceScores || companyRatings) && !companyRatingsLoading ? (
                    <div className="flex-shrink-0" style={{ width: 118 }}>
                      <TrustDial score={workplaceTrustScore} theme={theme} size={118} max={950} />
                    </div>
                  ) : null}
                </div>
                {companyRatingsLoading ? (
                  <div className="small" style={{ color: theme.textMute }}>Loading employee ratings…</div>
                ) : companyRatingsError ? (
                  <div className="small fw-semibold text-danger">{companyRatingsError}</div>
                ) : !companyRatings ? (
                  <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>
                    Enter a company name and search to see the employer score breakdown.
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>
                        Score breakdown
                      </div>
                      {hasWorkplaceScores ? (
                        <BreakdownGrid criteria={EMPLOYER_CRITERIA} score={ratingScore} theme={theme} />
                      ) : (
                        <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>
                          {companyRatings.found
                            ? "No employee ratings yet. Score breakdown updates after employees submit a workplace rating."
                            : "No WIBIL employer profile was found for this company yet."}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>
                        Private workplace ratings on file
                      </div>
                      {privateRatingCount > 0 ? (
                        <div
                          className="d-flex align-items-start gap-2 p-3 rounded-3"
                          style={{ background: theme.surfaceRaised, border: `1px solid ${theme.line}` }}
                        >
                          <Lock size={14} color={theme.accent} className="flex-shrink-0 mt-1" />
                          <div className="small mb-0" style={{ color: theme.text }}>
                            {privateRatingCount} private rating{privateRatingCount === 1 ? "" : "s"} on file.
                            Comments, individual scores, and proof are confidential and are not shown here.
                          </div>
                        </div>
                      ) : (
                        <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>
                          No private workplace ratings on file yet.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {openPositions.length > 0 && (
            <div className="card rounded-4 shadow-sm overflow-hidden" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
              <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between" style={{ borderColor: theme.line }}>
                <h4 className="h6 fw-bold mb-0" style={{ fontFamily: fonts.display }}>
                  Search Results: {openPositions.length} Position{openPositions.length === 1 ? "" : "s"}
                </h4>
              </div>
              <div>
                {openPositions.map((job, idx) => {
                  const meta = [job.company || targetCompany, job.location, yearsLabel(job)].filter(Boolean).join(" • ");
                  return (
                    <div
                      key={`${job.id || job.title}-${idx}`}
                      className="px-4 py-3 d-flex gap-3 align-items-center justify-content-between flex-wrap"
                      style={{ borderTop: idx ? `1px solid ${theme.line}` : "none" }}
                    >
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: 16 }}>
                          <Building2 size={16} color={theme.accent} className="flex-shrink-0" />
                          <span>{job.title}</span>
                        </div>
                        <div className="small mt-1" style={{ color: theme.textMute }}>{meta}</div>
                        {job.skills ? (
                          <div className="small mt-1.5 text-truncate" style={{ color: theme.accent, maxWidth: "100%" }}>
                            <strong>Skills:</strong> {job.skills}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex-shrink-0 mt-2 mt-md-0">
                        {job.url ? (
                          <a
                            href={job.url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary fw-bold d-inline-flex align-items-center gap-1"
                            style={{ borderColor: theme.accent, color: theme.accent }}
                          >
                            <ExternalLink size={13} /> Apply / View Posting
                          </a>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary fw-bold"
                            style={{ background: theme.accent, border: "none" }}
                            onClick={() => navigate("/apply-for-job")}
                          >
                            Apply on WIBIL
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {showLinkedInJobs && (
        <div className="mb-5" ref={linkedinJobsRef}>
          <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
            <h3 className="h5 fw-bold mb-0 text-uppercase tracking-wider small" style={{ color: theme.textMute, fontFamily: fonts.mono }}>
              LinkedIn jobs in your field{linkedinField ? ` — ${linkedinField}` : ""} ({linkedinJobs.length})
            </h3>
            <div className="d-flex align-items-center gap-2">
              {linkedinSearchUrl && (
                <button className="btn btn-sm btn-outline-primary fw-bold d-inline-flex align-items-center gap-1" onClick={() => window.open(linkedinSearchUrl, "_blank", "noopener,noreferrer")}>
                  <ExternalLink size={14} /> Open LinkedIn Jobs
                </button>
              )}
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowLinkedInJobs(false)}>
                Close
              </button>
            </div>
          </div>
          <div className="row g-3">
            {linkedinJobs.length === 0 ? (
              <div className="col-12">
                <div className="card p-4 rounded-4" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
                  <Briefcase size={22} className="mb-2" color={theme.textMute} />
                  <h4 className="h6 fw-bold mb-1">No LinkedIn jobs loaded yet</h4>
                  <p className="small text-secondary mb-0">{linkedinJobsMessage || "Try again, or open LinkedIn Jobs in a new tab."}</p>
                </div>
              </div>
            ) : linkedinJobs.map((job) => (
              <div key={job.id} className="col-12 col-lg-6">
                <div className="card h-100 p-4 rounded-4 shadow-sm" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-bold">{job.role || job.title}</div>
                      <div className="small text-secondary mt-1">
                        <Building2 size={12} className="me-1" />{job.company_name || job.company || "Company"}
                        {(job.location) ? ` • ${job.location}` : ""}
                      </div>
                      {job.postedTime && <div className="small text-secondary mt-1"><Clock size={12} className="me-1" />{job.postedTime}</div>}
                    </div>
                    <span className="badge rounded-pill" style={{ background: "#0A66C2", color: "#fff", fontSize: 10 }}>LinkedIn</span>
                  </div>
                  <button
                    className="btn btn-sm btn-primary fw-bold mt-3"
                    onClick={() => window.open(job.jobUrl || job.applyUrl || linkedinSearchUrl, "_blank", "noopener,noreferrer")}
                  >
                    <ExternalLink size={13} className="me-1" /> View LinkedIn job
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Applied Jobs Pipeline Section */}
        <div className="mb-5">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h3 className="h5 fw-bold mb-0 text-uppercase tracking-wider small" style={{ color: theme.textMute, fontFamily: fonts.mono }}>
              📋 ACTIVE JOB APPLICATIONS ({appliedJobs.length})
            </h3>
            <button className="btn btn-sm btn-outline-primary fw-bold d-inline-flex align-items-center gap-1" onClick={() => navigate("/apply-for-job")}>
              <Plus size={14} /> Apply for Role
            </button>
          </div>

          <div className="row g-3">
            {appliedJobs.length === 0 ? (
              <div className="col-12">
                <div className="card p-4 rounded-4 text-center" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
                  <Briefcase size={28} className="mb-2" color={theme.textMute} />
                  <h4 className="h6 fw-bold mb-1">No live applications yet</h4>
                  <p className="small text-secondary mb-3">Dummy applications have been removed. Submit a real application to see it here.</p>
                  <button className="btn btn-sm btn-primary fw-bold px-3" onClick={() => navigate("/apply-for-job")}>
                    Apply for a role
                  </button>
                </div>
              </div>
            ) : appliedJobs.map(job => {
              const status = statusStyle(job.status);
              const appliedDate = job.created_at ? String(job.created_at).slice(0, 10) : "";
              return (
              <div key={job.id} className="col-12 col-lg-4">
                <div 
                  className="card h-100 p-4 rounded-4 shadow-sm position-relative overflow-hidden" 
                  style={{ 
                    background: theme.surface, 
                    border: `1px solid ${theme.line}`,
                    color: theme.text
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="badge px-3 py-1.5 rounded-pill fw-bold" style={{ background: `${status.color}18`, color: status.color, border: `1px solid ${status.color}44`, fontSize: 11 }}>
                      {status.label}
                    </span>
                    <span className="small text-secondary">{appliedDate}</span>
                  </div>

                  <h4 className="h6 fw-bold mb-1" style={{ color: theme.text }}>{job.role || "Open role"}</h4>
                  <div className="small fw-semibold mb-2" style={{ color: theme.accent }}>
                    <Building2 size={13} className="me-1 d-inline" />{job.company_name || "Recruiter"}
                  </div>

                  <div className="small text-secondary mb-3">
                    {job.location && <div>📍 {job.location}</div>}
                    {job.salary_range && <div>💰 {job.salary_range}</div>}
                    {job.interview_date && (
                      <div className="mt-2 p-2 rounded-2 bg-body-tertiary border text-body" style={{ fontSize: 12 }}>
                        <strong>Interview:</strong> {job.interview_date}
                      </div>
                    )}
                  </div>

                  <div className="mt-auto pt-2 d-flex align-items-center justify-content-between border-top" style={{ borderColor: theme.line }}>
                    <span className="small text-success fw-bold d-inline-flex align-items-center gap-1">
                      <CheckCircle2 size={12} /> Live application
                    </span>
                    <button className="btn btn-sm btn-link text-primary text-decoration-none fw-bold p-0" onClick={() => navigate("/apply-for-job")}>
                      View application <ArrowRight size={12} className="d-inline ms-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            ); })}
          </div>
        </div>

        {/* Recruiter Visibility & AI Job Matches */}
        <div className="row g-4 mb-5">
          
          <div className="col-12 col-md-6">
            <div className="card h-100 p-4 rounded-4 shadow-sm" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
              <h4 className="h6 fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: theme.text }}>
                <Sparkles size={18} color={theme.accent} /> Recruiter Interest & Analytics
              </h4>
              <div className="d-flex flex-column gap-3 small">
                <div className="d-flex justify-content-between align-items-center p-2 rounded-3 bg-body-tertiary">
                  <span className="text-secondary">Active applications:</span>
                  <strong className="fs-6 text-primary">{appliedJobs.length}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 rounded-3 bg-body-tertiary">
                  <span className="text-secondary">Shortlists:</span>
                  <strong className="fs-6 text-success">{appliedJobs.filter(j => String(j.status || "").toLowerCase() === "shortlisted").length}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 rounded-3 bg-body-tertiary">
                  <span className="text-secondary">Open recruiter jobs:</span>
                  <strong className="fs-6 text-warning">{matchedJobs.length}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card h-100 p-4 rounded-4 shadow-sm" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
              <h4 className="h6 fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: theme.text }}>
                <Send size={18} className="text-success" /> WIBIL recruiter jobs in your field
              </h4>
              <p className="small text-secondary mb-3">
                Roles posted by recruiters on WIBIL for {linkedinField || profile.title || "your role"}. Use Check Open Positions to fetch a company’s matching openings, or LinkedIn Jobs in my field for LinkedIn listings.
              </p>

              <div className="d-flex flex-column gap-2">
                {matchedJobs.length === 0 ? (
                  <div className="p-3 rounded-3 border bg-body-tertiary small text-secondary">
                    No matching WIBIL recruiter jobs yet. Use LinkedIn Jobs in my field for LinkedIn listings.
                  </div>
                ) : matchedJobs.slice(0, 5).map((item) => (
                  <div key={item.id} className="p-3 rounded-3 border bg-body-tertiary d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-bold small text-body">{item.role || item.title}</div>
                      <div className="small text-secondary">{item.company_name || item.company}{item.location ? ` • ${item.location}` : ""}</div>
                      {item.salary_range && <div className="small fw-semibold text-success">{item.salary_range}</div>}
                    </div>
                    <button className="btn btn-sm btn-primary fw-bold px-3 py-1.5" onClick={async () => {
                      if (item.jobUrl && !item.id) {
                        window.open(item.jobUrl || item.applyUrl, "_blank", "noopener,noreferrer");
                        return;
                      }
                      if (user?.id && item.id) {
                        try {
                          await api.applyToJob({ jobId: item.id, employeeId: user.id });
                          const apps = await api.getSeekerApplications(user.id);
                          setAppliedJobs(Array.isArray(apps) ? apps : []);
                          return;
                        } catch (err) {
                          navigate(`/apply-for-job?jobId=${encodeURIComponent(item.id)}`);
                          return;
                        }
                      }
                      if (item.jobUrl || item.applyUrl) window.open(item.jobUrl || item.applyUrl, "_blank", "noopener,noreferrer");
                      else navigate("/apply-for-job");
                    }}>
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}

function workplaceTrustFromScore(score, ratingCount) {
  const s = score || {};
  const pillars = [
    Number(s.avg_salary || s.avg_salary_timeliness || 0),
    Number(s.avg_environment || 0),
    Number(s.avg_management || 0),
    Number(s.avg_growth || 0),
    Number(s.avg_location || s.avg_location_score || 0)
  ].filter((n) => n > 0);
  if (pillars.length) {
    return Math.min(950, Math.round(300 + (pillars.reduce((a, b) => a + b, 0) / pillars.length) * 65));
  }
  const ts = Number(s.trust_score);
  if (Number(ratingCount) > 0 && Number.isFinite(ts) && ts > 300) return Math.round(Math.min(950, ts));
  return 300;
}
